import * as z from 'zod';

export const ListSiteScriptsRequest = z.object({
  options: z
    .object({
      pageUrl: z
        .string()
        .describe(
          'Full canonical URL of the page where SEO tags should be resolved.\nWhen provided together with `page_name`, the response includes SEO tags\nfor the page as additional HEAD-position site scripts.'
        )
        .max(2048)
        .optional()
        .nullable(),
      pageName: z
        .string()
        .describe(
          'Page name identifier matching the page in the Wix site structure.\nRequired together with `page_url` to resolve SEO tags.'
        )
        .max(255)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListSiteScriptsResponse = z.object({
  siteScripts: z
    .array(
      z.object({
        position: z
          .enum(['UNKNOWN_POSITION', 'HEAD', 'BODY_START', 'BODY_END'])
          .describe('Position where the script is placed on the page.')
          .optional(),
        html: z.string().describe('Script HTML').max(15000).optional(),
      })
    )
    .optional(),
});
export const GetEssentialPropertiesRequest = z.object({});
export const GetEssentialPropertiesResponse = z.object({
  properties: z
    .object({
      locale: z
        .object({
          languageCode: z
            .string()
            .describe(
              'Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
            )
            .optional(),
          country: z
            .string()
            .describe(
              'Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.'
            )
            .optional(),
        })
        .describe('Site locale.')
        .optional(),
      language: z
        .string()
        .describe(
          'Site language.\nTwo-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
        )
        .optional()
        .nullable(),
      paymentCurrency: z
        .string()
        .describe(
          'Site currency format used to bill customers.\nThree-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .max(50)
        .optional()
        .nullable(),
      multilingual: z
        .object({
          autoRedirect: z.boolean().describe('Auto redirect.').optional(),
          supportedLanguages: z
            .array(
              z.object({
                languageCode: z.string().describe('Language code.').optional(),
                countryCode: z.string().describe('Country code.').optional(),
                isPrimary: z
                  .boolean()
                  .describe('Is primary language.')
                  .optional(),
                isVisitorPrimary: z
                  .boolean()
                  .describe('Is visitor primary language.')
                  .optional(),
                locale: z
                  .object({
                    languageCode: z
                      .string()
                      .describe(
                        'Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
                      )
                      .optional(),
                    country: z
                      .string()
                      .describe(
                        'Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.'
                      )
                      .optional(),
                  })
                  .describe('Locale.')
                  .optional(),
                resolutionMethod: z
                  .enum(['QUERY_PARAM', 'SUBDOMAIN', 'SUBDIRECTORY'])
                  .describe(
                    'How the language will be resolved. For internal use.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe('Multilingual settings.')
        .optional(),
    })
    .describe('Properties of the site')
    .optional(),
});
export const GetSiteMetadataRequest = z.object({
  options: z
    .object({
      pageUrl: z
        .string()
        .describe(
          'Full canonical URL of the page where SEO tags should be resolved.\nWhen provided together with `page_name`, the response includes SEO tags\nfor the page as additional HEAD-position site scripts.'
        )
        .max(2048)
        .optional()
        .nullable(),
      pageName: z
        .string()
        .describe(
          'Page name identifier matching the page in the Wix site structure.\nRequired together with `page_url` to resolve SEO tags.'
        )
        .max(255)
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetSiteMetadataResponse = z.object({
  siteScripts: z
    .array(
      z.object({
        position: z
          .enum(['UNKNOWN_POSITION', 'HEAD', 'BODY_START', 'BODY_END'])
          .describe('Position where the script is placed on the page.')
          .optional(),
        html: z.string().describe('Script HTML').max(15000).optional(),
      })
    )
    .optional(),
  properties: z
    .object({
      locale: z
        .object({
          languageCode: z
            .string()
            .describe(
              'Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
            )
            .optional(),
          country: z
            .string()
            .describe(
              'Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.'
            )
            .optional(),
        })
        .describe('Site locale.')
        .optional(),
      language: z
        .string()
        .describe(
          'Site language.\nTwo-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
        )
        .optional()
        .nullable(),
      paymentCurrency: z
        .string()
        .describe(
          'Site currency format used to bill customers.\nThree-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .max(50)
        .optional()
        .nullable(),
      multilingual: z
        .object({
          autoRedirect: z.boolean().describe('Auto redirect.').optional(),
          supportedLanguages: z
            .array(
              z.object({
                languageCode: z.string().describe('Language code.').optional(),
                countryCode: z.string().describe('Country code.').optional(),
                isPrimary: z
                  .boolean()
                  .describe('Is primary language.')
                  .optional(),
                isVisitorPrimary: z
                  .boolean()
                  .describe('Is visitor primary language.')
                  .optional(),
                locale: z
                  .object({
                    languageCode: z
                      .string()
                      .describe(
                        'Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.'
                      )
                      .optional(),
                    country: z
                      .string()
                      .describe(
                        'Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.'
                      )
                      .optional(),
                  })
                  .describe('Locale.')
                  .optional(),
                resolutionMethod: z
                  .enum(['QUERY_PARAM', 'SUBDOMAIN', 'SUBDIRECTORY'])
                  .describe(
                    'How the language will be resolved. For internal use.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe('Multilingual settings.')
        .optional(),
    })
    .describe('Essential properties of the site.')
    .optional(),
});
